import * as React from "react";
import { format, parseISO } from "date-fns";
import { CalendarIcon, X } from "lucide-react";

import { cn } from "../../lib/utils";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { pickerTriggerClass, type TriggerVariant } from "./trigger-recipes";

/**
 * Job D — COMPOSE (`docs/picker-taxonomy.md` §1.5, §2.4, §3.4).
 *
 * The panel is a calendar grid, not a list, so this is a `Popover` and the
 * trigger carries its **domain glyph and no caret** — a caret promises a value
 * list, and there isn't one. Geometry still comes from
 * `pickerTriggerClass(variant)`, so a date row and a status row in the same
 * rail line up exactly.
 */

export type DatePickerVariant = Exclude<TriggerVariant, "nav">;

export interface DatePickerProps {
  value?: string;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  clearable?: boolean;
  /**
   * Trigger geometry — `"field"` in forms and dialogs, `"inline"` in a
   * `PropertyRow`, `"compact"` in a toolbar or chip row. Pass this instead of
   * a class string so every date control matches the pickers beside it.
   */
  variant?: DatePickerVariant;
  /**
   * Field name for the trigger. The trigger is otherwise named by its value
   * ("Sep 30, 2026", "No due date"), which says nothing about *which* date it
   * is — pass this wherever the visible label is only adjacent text, e.g. a
   * `PropertyRow`. Rendered as "{name}: {value}" so both are announced.
   */
  ariaLabel?: string;
  /**
   * Earliest selectable day, `yyyy-MM-dd`. Days before it render disabled.
   *
   * Use for a date that is only meaningful in the future — a share-link expiry,
   * a scheduled send. The month grid shows the trailing days of the previous
   * month, so without this an invalid date is one mis-click away, and catching
   * it on submit teaches the user nothing about which days were allowed.
   * Omitted by default: a due date or a start date may legitimately be past.
   */
  minDate?: string;
  /** Latest selectable day, `yyyy-MM-dd`. Days after it render disabled. */
  maxDate?: string;
}

const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      placeholder = "Pick a date",
      disabled = false,
      className,
      clearable = true,
      variant = "field",
      ariaLabel,
      minDate,
      maxDate,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const date = value ? parseISO(value) : undefined;
    const valueText = date ? format(date, "MMM d, yyyy") : placeholder;

    // `parseISO("2026-08-05")` lands on local midnight, so comparing it against
    // a day in the grid (also local midnight) includes the boundary day itself.
    const bounds = React.useMemo(() => {
      const min = minDate ? parseISO(minDate) : null;
      const max = maxDate ? parseISO(maxDate) : null;
      if (!min && !max) return { min, max, disabled: undefined };
      return {
        min,
        max,
        disabled: [
          ...(min ? [{ before: min }] : []),
          ...(max ? [{ after: max }] : []),
        ],
      };
    }, [minDate, maxDate]);
    const showClear = clearable && Boolean(date) && !disabled;

    const clear = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onChange?.(null);
      },
      [onChange],
    );

    return (
      <Popover open={open} onOpenChange={setOpen}>
        {/*
          The clear affordance is a **sibling** of the trigger. It used to be a
          `role="button" tabIndex={-1}` span *inside* the trigger `<button>` —
          invalid nesting, and unreachable by keyboard by construction.
        */}
        <span
          className={cn(
            "relative inline-flex items-center",
            variant === "compact" ? "max-w-full shrink-0" : "w-full",
          )}
        >
          <PopoverTrigger asChild>
            <button
              ref={ref}
              type="button"
              disabled={disabled}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-label={ariaLabel ? `${ariaLabel}: ${valueText}` : undefined}
              data-slot="date-picker-trigger"
              data-placeholder={date ? undefined : ""}
              className={cn(
                pickerTriggerClass(variant),
                // The recipe's `justify-between` is for a value + caret; a date
                // trigger leads with its glyph and has no caret, so the row
                // packs from the left.
                "justify-start",
                showClear && "pr-7",
                className,
              )}
            >
              <CalendarIcon
                aria-hidden
                className={cn(
                  "text-text-tertiary",
                  variant === "compact" ? "size-3" : "size-3.5",
                )}
              />
              <span
                className={cn(
                  "min-w-0 truncate",
                  date ? "tabular-nums text-text" : "text-text-tertiary",
                )}
              >
                {valueText}
              </span>
            </button>
          </PopoverTrigger>
          {showClear ? (
            <button
              type="button"
              onClick={clear}
              aria-label={ariaLabel ? `Clear ${ariaLabel}` : "Clear date"}
              className={cn(
                "absolute right-1.5 grid size-4 place-items-center rounded-xs",
                "text-text-tertiary hover:bg-hover hover:text-text",
                "transition-[background-color,color] duration-100 ease-out-fast",
                "focus-visible:outline-none focus-fill",
              )}
            >
              <X className="size-3" aria-hidden />
            </button>
          ) : null}
        </span>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            // `defaultMonth` so a bounded picker opens on the first month that
            // actually has selectable days rather than on today's, which may be
            // entirely disabled.
            defaultMonth={date ?? bounds.min ?? undefined}
            startMonth={bounds.min ?? undefined}
            endMonth={bounds.max ?? undefined}
            disabled={bounds.disabled}
            onSelect={(d) => {
              onChange?.(d ? format(d, "yyyy-MM-dd") : null);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    );
  },
);
DatePicker.displayName = "DatePicker";

export { DatePicker };
